import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { ALL_NAV_ITEMS } from "@/lib/nav";
import { useUserUiPreferences } from "@/lib/UserUiPreferencesProvider";
import { useNavBadges, type NavBadges } from "@/navigation/useNavBadges";

const SIDEBAR_COLLAPSED_KEY = "eh-nav-sidebar-collapsed";
const RECENT_NAV_KEY = "eh-nav-recent";
const MAX_RECENT = 6;

interface NavContextValue {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  moreMenuOpen: boolean;
  setMoreMenuOpen: (open: boolean) => void;
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  openCommandPalette: () => void;
  recentHrefs: string[];
  favoriteHrefs: string[];
  toggleFavorite: (href: string) => void;
  badges: NavBadges;
}

const NavContext = createContext<NavContextValue | null>(null);

function readJsonArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeJsonArray(key: string, value: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

function isTrackablePath(pathname: string): boolean {
  if (pathname === "/" || pathname.startsWith("/login")) return false;
  if (pathname.startsWith("/stammdaten")) return true;
  return ALL_NAV_ITEMS.some(
    (item) =>
      item.href !== "#more" &&
      (pathname === item.href || pathname.startsWith(`${item.href}/`)),
  );
}

export function NavProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  });
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [recentHrefs, setRecentHrefs] = useState<string[]>(() => readJsonArray(RECENT_NAV_KEY));
  const { favoriteHrefs, toggleFavorite: toggleStoredFavorite } = useUserUiPreferences();
  const badges = useNavBadges(true);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsedState((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  }, []);

  const openCommandPalette = useCallback(() => {
    setCommandPaletteOpen(true);
  }, []);

  const toggleFavorite = useCallback(
    (href: string) => {
      const adding = !favoriteHrefs.includes(href);
      toggleStoredFavorite(href);
      if (adding) {
        setRecentHrefs((recent) => {
          const updated = recent.filter((h) => h !== href);
          writeJsonArray(RECENT_NAV_KEY, updated);
          return updated;
        });
      }
    },
    [favoriteHrefs, toggleStoredFavorite],
  );

  useEffect(() => {
    setMoreMenuOpen(false);
    setCommandPaletteOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isTrackablePath(pathname)) return;
    setRecentHrefs((prev) => {
      if (favoriteHrefs.includes(pathname)) {
        const withoutFavorite = prev.filter((h) => h !== pathname);
        if (withoutFavorite.length === prev.length) return prev;
        writeJsonArray(RECENT_NAV_KEY, withoutFavorite);
        return withoutFavorite;
      }
      if (prev[0] === pathname) return prev;
      const next = [pathname, ...prev.filter((h) => h !== pathname)].slice(0, MAX_RECENT);
      writeJsonArray(RECENT_NAV_KEY, next);
      return next;
    });
  }, [pathname, favoriteHrefs]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const value = useMemo<NavContextValue>(
    () => ({
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebar,
      moreMenuOpen,
      setMoreMenuOpen,
      commandPaletteOpen,
      setCommandPaletteOpen,
      openCommandPalette,
      recentHrefs,
      favoriteHrefs,
      toggleFavorite,
      badges,
    }),
    [
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebar,
      moreMenuOpen,
      commandPaletteOpen,
      openCommandPalette,
      recentHrefs,
      favoriteHrefs,
      toggleFavorite,
      badges,
    ],
  );

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNav(): NavContextValue {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error("useNav must be used within NavProvider");
  return ctx;
}
